import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import {
  Card,
  Pill,
  LabelMeta,
  LoadingState,
  EmptyState,
  AIUnavailableBanner,
  StaleEvaluationBanner,
  theme,
  capitalize,
} from "../components/ui";
import { AiProcessingDisplayStatusEnum } from "Constants/Enums/AiProcessingDisplayStatusEnum";

// Merged Talent Profile + AI Insights — ATS-grade dense view.
//
// Hierarchy per Hammad:
//   1. CORE IDENTITY at the top — title, years, education, GitHub, LinkedIn
//   2. AI Decision — the answer (big score + decision + recommendation)
//   3. Skills match (2 columns) — critical evidence
//   4. Strengths / Concerns (2 columns) — signal
//   5. Domain + Work Experience — context
//   6. Top Highlights matching the role
//
// All sections Hammad mandated are kept. Density is reduced via smaller padding,
// tighter typography, lower per-section item caps.
function TalentProfileTab({ jobApplicationId, jobPostingSkills = [], isInternal = false }) {
  const [profileData, setProfileData] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [evaluationExists, setEvaluationExists] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingEvaluation, setLoadingEvaluation] = useState(true);
  const [profileNotFound, setProfileNotFound] = useState(false);
  // Per Mudassir (June-02): the AI evaluation GET now returns optional
  // freshness status objects. Captured defensively — absent on older responses.
  const [evaluationStatus, setEvaluationStatus] = useState(null);
  const [talentProfileStatus, setTalentProfileStatus] = useState(null);

  useEffect(() => {
    if (!jobApplicationId) return;
    fetchProfile();
    fetchEvaluation();
  }, [jobApplicationId]);

  async function fetchProfile() {
    try {
      setLoadingProfile(true);
      setProfileNotFound(false);
      const response = await axiosInstance.get(
        API.applicantTalentProfile.getDetailByApplication(jobApplicationId)
      );
      setProfileData(response.data);
    } catch (error) {
      console.error("Error fetching talent profile:", error);
      if (error.response?.status === 404) setProfileNotFound(true);
    } finally {
      setLoadingProfile(false);
    }
  }

  async function fetchEvaluation() {
    try {
      setLoadingEvaluation(true);
      const response = await axiosInstance.get(
        API.aiEvaluation.getByApplication(jobApplicationId)
      );
      const data = response.data || {};
      // Per Mudassir May-22: response is now flat (no { exists, evaluation }
      // wrapper). Backwards-compat: if `exists` is present, use the old
      // wrapper; otherwise treat the response itself as the evaluation.
      const hasWrapper = Object.prototype.hasOwnProperty.call(data, "exists");
      const evaluation = hasWrapper ? data.evaluation || null : data;
      const exists = hasWrapper
        ? !!data.exists
        : evaluation != null && evaluation.jobApplicationId != null;
      setEvaluationExists(exists);
      setEvaluation(evaluation);
      // Optional freshness status (June-02). Lives at the response top level
      // (sibling of `evaluation`) in both the wrapped and flat shapes. Absent
      // on older backends → stays null → no banner renders.
      setEvaluationStatus(data.evaluationStatus || null);
      setTalentProfileStatus(data.talentProfileStatus || null);
    } catch (error) {
      console.error("Error fetching AI evaluation:", error);
    } finally {
      setLoadingEvaluation(false);
    }
  }

  if (loadingProfile) return <LoadingState label="Loading talent profile..." />;
  if (profileNotFound || !profileData) {
    return (
      <EmptyState
        icon="🧠"
        title="Talent profile not generated yet"
        hint="Profile will be available once the applicant data has been parsed."
      />
    );
  }

  // ---------- Derive data with fallbacks ----------
  const skills = profileData.skills || [];
  const domains = profileData.domainKnowledges || [];
  const educations = profileData.educations || [];
  const certifications = profileData.certifications || [];
  const experiences = profileData.experiences || [];
  const achievements = profileData.keyAchievements || [];

  const derivedTitle =
    profileData.currentTitle ||
    experiences.find((e) => e.isCurrent)?.jobTitle ||
    experiences[0]?.jobTitle ||
    null;

  const derivedYears = (() => {
    if (
      profileData.totalExperienceYears != null &&
      profileData.totalExperienceYears > 0
    ) {
      return Number(profileData.totalExperienceYears);
    }
    if (!experiences.length) return null;
    const months = experiences.reduce((sum, exp) => {
      if (!exp.startDate) return sum;
      const start = new Date(exp.startDate);
      const end = exp.isCurrent || !exp.endDate ? new Date() : new Date(exp.endDate);
      const diffMonths =
        (end.getFullYear() - start.getFullYear()) * 12 +
        (end.getMonth() - start.getMonth());
      return sum + Math.max(0, diffMonths);
    }, 0);
    return months > 0 ? +(months / 12).toFixed(1) : null;
  })();

  // Per Sara (May-22): skills can be either string OR object
  // ({name: "..."} or {skillName: "..."}). The Candidate Skills panel handles
  // both via a fallback chain; normalize must too — otherwise objects collapse
  // to "[object Object]" and Missing Skills falsely flags every required skill.
  const normalize = (s) => {
    const text =
      typeof s === "string"
        ? s
        : s?.name || s?.skillName || s?.label || "";
    return String(text).trim().toLowerCase();
  };
  const jobSkillsNormalized = (jobPostingSkills || []).map(normalize);
  const candidateSkillsNormalized = skills.map(normalize);

  const isSkillMatched = (skill) =>
    jobSkillsNormalized.includes(normalize(skill));
  const candidateHasJobSkill = (jobSkill) =>
    candidateSkillsNormalized.includes(normalize(jobSkill));

  const matchedAchievements = achievements
    .map((ach) => (typeof ach === "string" ? ach : ach?.description || ""))
    .filter((text) => {
      const t = normalize(text);
      return jobSkillsNormalized.some((js) => js && t.includes(js));
    })
    .slice(0, 3);
  const topAchievements =
    matchedAchievements.length > 0
      ? matchedAchievements
      : achievements
          .slice(0, 3)
          .map((ach) =>
            typeof ach === "string" ? ach : ach?.description || ""
          );

  const hasEvaluation = evaluationExists && !!evaluation;
  // AI-unavailable detection: backend returns 200 with null AI fields when
  // the external AI service is down (per Mudassir May-21). Treat any case
  // where evaluation exists but the key fields are null as "AI is down" so
  // we render the banner instead of a misleading 0-fit-score card.
  const aiUnavailable =
    hasEvaluation &&
    evaluation.fitScore == null &&
    evaluation.decision == null;
  // Soften the AI's raw "decision" enum into a non-directive label so HR sees
  // the AI as advisory, not the deciding party. Final hiring decision is
  // always HR's — wording reflects that.
  const decisionText = hasEvaluation
    ? softenDecision(evaluation.decision)
    : "—";
  const confidence = hasEvaluation ? evaluation.confidence || "—" : "—";
  const fitScore = hasEvaluation ? evaluation.fitScore ?? 0 : null;
  const skillMatchPct = hasEvaluation
    ? evaluation.skillMatchPercentage ?? 0
    : null;
  // Per Sai (May-22): surface essential-skills match % + scoreBreakdown so
  // Hammad can audit how the fit score was calculated.
  const essentialSkillsPct = hasEvaluation
    ? evaluation.essentialSkillsMatchPercentage
    : null;
  const scoreBreakdown = hasEvaluation ? evaluation.scoreBreakdown : null;
  const domainFit = hasEvaluation ? evaluation.domainFit || "—" : "—";

  // Derive variant from the raw enum (not the softened label) so renaming
  // labels doesn't accidentally change the color.
  const rawDecision = String(evaluation?.decision || "").toLowerCase();
  const decisionVariant = /move_forward|strong|proceed/.test(rawDecision)
    ? "success"
    : /reject|low/.test(rawDecision)
    ? "danger"
    : "warning";

  const fitColor =
    fitScore == null
      ? theme.textMuted
      : fitScore >= 75
      ? "#22c55e"
      : fitScore >= 50
      ? "#f59e0b"
      : "#ef4444";

  return (
    <div style={{ padding: "1.25rem 1.5rem" }}>
      {aiUnavailable && <AIUnavailableBanner />}
      {/* Soft freshness alerts (June-02). Each renders only when the backend
          reports a non-Current status; the evaluation one is suppressed while
          the dedicated "AI unavailable" banner already shows, to avoid stacking. */}
      {!aiUnavailable && (
        <StaleEvaluationBanner
          status={evaluationStatus?.status}
          lastSuccessfulAt={evaluationStatus?.lastSuccessfulAt}
          label="AI evaluation"
        />
      )}
      <StaleEvaluationBanner
        status={talentProfileStatus?.status}
        lastSuccessfulAt={talentProfileStatus?.lastSuccessfulAt}
        label="Talent profile"
      />
      {/* ============== 1. AI DECISION (TOP — the primary scan target) ============== */}
      {hasEvaluation && !aiUnavailable && (
        <div
          className="ai-decision-grid"
          style={{
            background:
              "linear-gradient(135deg, #e6f4ea 0%, #f7faf7 50%, #eaf4ee 100%)",
            borderRadius: "24px",
            padding: "1.1rem 1.4rem",
            marginBottom: "1rem",
            border: `1px solid ${theme.border}`,
            boxShadow: theme.shadowCard,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "1.5rem",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Pill
                variant={decisionVariant}
                style={{ fontSize: "0.85rem", padding: "6px 18px", fontWeight: 700 }}
              >
                🤖 AI suggests: {decisionText}
              </Pill>
              <Tooltip
                title="AI's confidence in this assessment — driven by how complete the input data is (resume, interview ratings, feedback) and how strong the signals are."
                placement="top"
              >
                <span
                  style={{
                    fontSize: "0.78rem",
                    color: theme.textMuted,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    cursor: "help",
                  }}
                >
                  {confidence} confidence
                  <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.7 }} />
                </span>
              </Tooltip>
              <span style={{ color: theme.border }}>·</span>
              <span style={{ fontSize: "0.85rem", color: theme.text, fontWeight: 600 }}>
                Skill match {skillMatchPct}%
              </span>
              {essentialSkillsPct != null && (
                <>
                  <span style={{ color: theme.border }}>·</span>
                  <span
                    style={{
                      fontSize: "0.85rem",
                      color: theme.text,
                      fontWeight: 600,
                    }}
                  >
                    Essential {essentialSkillsPct}%
                  </span>
                </>
              )}
              <span style={{ color: theme.border }}>·</span>
              <span style={{ fontSize: "0.85rem", color: theme.text, fontWeight: 600 }}>
                Domain fit: {capitalize(domainFit)}
              </span>
            </div>
            {evaluation.recommendation && (
              <>
                <p
                  style={{
                    margin: "10px 0 0 0",
                    lineHeight: 1.45,
                    fontSize: "0.88rem",
                    color: theme.text,
                  }}
                >
                  {softenRecommendation(evaluation.recommendation)}
                </p>
                <p
                  style={{
                    margin: "6px 0 0 0",
                    fontSize: "0.72rem",
                    color: theme.textMuted,
                    fontStyle: "italic",
                  }}
                >
                  AI-generated suggestion to assist HR — final hiring decision
                  is made by HR.
                </p>
              </>
            )}
            {/* Per Sara (May-23): re-scoring is silent — HR needs to know
                the score is fresh. Show the last-update timestamp so HR
                knows when the AI last re-evaluated the candidate. */}
            {evaluation.lastModifiedOn && (
              <div
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "0.7rem",
                  color: theme.textLabel,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>🕒</span>
                <span>
                  Last AI evaluation: {formatLastUpdate(evaluation.lastModifiedOn)}
                </span>
              </div>
            )}
          </div>
          <div style={{ textAlign: "center", minWidth: "100px" }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: "2.6rem",
                color: fitColor,
                lineHeight: 1,
              }}
            >
              {fitScore}
            </div>
            <div
              style={{
                fontSize: "0.7rem",
                color: theme.textLabel,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginTop: "2px",
              }}
            >
              Fit · /100
            </div>
            <div
              style={{
                background: "#e4eaf2",
                borderRadius: "20px",
                marginTop: "6px",
                height: "6px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min(100, Math.max(0, fitScore))}%`,
                  background: fitColor,
                  height: "100%",
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ============== 1b. SCORE BREAKDOWN (audit trail for the fit score) ============== */}
      {hasEvaluation && !aiUnavailable && scoreBreakdown && (
        <ScoreBreakdownCard
          breakdown={scoreBreakdown}
          fitScore={fitScore}
          isInternal={isInternal}
        />
      )}

      {/* ============== 2. CORE IDENTITY ============== */}
      <Card title="Core Identity" icon="📌" style={{ padding: "1.1rem 1.4rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: "14px 28px",
          }}
        >
          <KeyVal
            label="Current Title"
            value={derivedTitle || <NotProvided />}
          />
          <KeyVal
            label="Experience"
            value={
              derivedYears != null
                ? `${derivedYears} ${derivedYears === 1 ? "year" : "years"}`
                : <NotProvided />
            }
          />
          <KeyVal
            label="Education"
            value={
              educations.length === 0 ? (
                <NotProvided />
              ) : (
                educations
                  .map(
                    (e) =>
                      `${e.degree || ""}${
                        e.fieldOfStudy ? `, ${e.fieldOfStudy}` : ""
                      }`.trim()
                  )
                  .join(" · ")
              )
            }
            secondary={
              educations.length > 0
                ? educations
                    .map((e) => e.institution)
                    .filter(Boolean)
                    .join(" · ")
                : null
            }
          />
          <KeyVal
            label="GitHub"
            value={
              profileData.gitHubUrl ? (
                <a
                  href={profileData.gitHubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  {profileData.gitHubUrl.replace(/^https?:\/\//, "")}
                </a>
              ) : (
                <NotProvided />
              )
            }
          />
          <KeyVal
            label="LinkedIn"
            value={
              profileData.linkedInUrl ? (
                <a
                  href={profileData.linkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                >
                  {profileData.linkedInUrl.replace(/^https?:\/\//, "")}
                </a>
              ) : (
                <NotProvided />
              )
            }
          />
        </div>
        {certifications.length > 0 && (
          <div
            style={{
              marginTop: "12px",
              paddingTop: "10px",
              borderTop: `1px dashed ${theme.borderSoft}`,
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                color: theme.textLabel,
                fontWeight: 600,
                marginRight: "4px",
              }}
            >
              📜 Certifications
            </span>
            {certifications.map((c, idx) => (
              <Pill key={idx} variant="skill" style={{ fontSize: "0.72rem" }}>
                {c.name}
                {c.year ? ` · ${c.year}` : ""}
              </Pill>
            ))}
          </div>
        )}
      </Card>

      {/* ============== 3. SKILLS — 2 COLUMNS ============== */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "1rem",
        }}
      >
        <CompactCard title={`Candidate Skills · ${skills.length}`} icon="🛠️">
          {skills.length === 0 ? (
            <Muted>No skills parsed.</Muted>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
              {skills.map((skill, idx) => {
                const label =
                  typeof skill === "string"
                    ? skill
                    : skill?.name || skill?.skillName || "";
                const matched = isSkillMatched(skill);
                return (
                  <span
                    key={idx}
                    style={{
                      padding: "3px 10px",
                      borderRadius: "40px",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      background: matched ? "#dff5e3" : "#f0f4f0",
                      color: matched ? "#0f5a2a" : theme.textMuted,
                      border: matched
                        ? "1.5px solid #2b7e3a"
                        : "1px solid transparent",
                    }}
                  >
                    {matched && "✓ "}
                    {label}
                  </span>
                );
              })}
            </div>
          )}
        </CompactCard>

        {(() => {
          // Per Sai (May-25): the AI evaluation now returns nested
          // essentialSkills + niceToHaveSkills with explicit matched /
          // matchedViaInference / missing arrays — render those when
          // available so HR sees inference reasoning + the essential vs
          // nice-to-have separation. Fallback to the legacy flat
          // missingSkills view when the new schema isn't on the response
          // (older candidates pre-migration).
          const essentialSkills = hasEvaluation
            ? evaluation.essentialSkills
            : null;
          // Per Mudassir (May-26): BE now sends `requiredSkills` +
          // `additionalSkills` as separate fields to the AI (no more merging),
          // so `niceToHaveSkills` will actually populate when HR fills in the
          // "Additional Skills" dropdown on the JobPosting form. Bringing the
          // Nice-to-Have section back on FE to render that data.
          const niceToHaveSkills = hasEvaluation
            ? evaluation.niceToHaveSkills
            : null;
          const hasNestedSchema = !!(essentialSkills || niceToHaveSkills);

          if (hasNestedSchema) {
            return (
              <RequiredSkillsAssessment
                essential={essentialSkills}
                niceToHave={niceToHaveSkills}
              />
            );
          }

          // ----- Legacy fallback (pre-May-25 schema) -----
          const aiMissing =
            hasEvaluation && Array.isArray(evaluation.missingSkills)
              ? evaluation.missingSkills
              : null;
          const missing = aiMissing
            ? aiMissing
            : jobPostingSkills.filter((s) => !candidateHasJobSkill(s));
          return (
            <CompactCard
              title={
                jobPostingSkills.length === 0 && !aiMissing
                  ? "Required Skills"
                  : missing.length === 0
                  ? "Required Skills — all matched ✓"
                  : `Missing Skills · ${missing.length}`
              }
              icon="⚠️"
              accentColor={missing.length === 0 ? "#2b7e3a" : "#b13e3e"}
            >
              {jobPostingSkills.length === 0 && !aiMissing ? (
                <Muted>No skills listed on the job posting.</Muted>
              ) : missing.length === 0 ? (
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#0f5a2a",
                    fontWeight: 500,
                  }}
                >
                  ✓ Candidate has every required skill for this role.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                  {missing.map((skill, idx) => {
                    // AI evaluation embeds nuance in parentheses like
                    // "UI Design (depth unverified, implied by work history)".
                    // Split into label + hint so the row reads cleanly.
                    const raw = typeof skill === "string" ? skill : String(skill);
                    const parenStart = raw.indexOf("(");
                    const name =
                      parenStart > 0 ? raw.slice(0, parenStart).trim() : raw;
                    const note =
                      parenStart > 0
                        ? raw.slice(parenStart + 1).replace(/\)\s*$/, "").trim()
                        : null;
                    return (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          padding: "6px 12px",
                          borderRadius: "10px",
                          background: "#fff0ee",
                          borderLeft: "3px solid #b13e3e",
                          fontSize: "0.82rem",
                          fontWeight: 500,
                          color: "#7a1f1f",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span>{name}</span>
                          {!note && (
                            <span
                              style={{
                                fontSize: "0.72rem",
                                fontStyle: "italic",
                                opacity: 0.85,
                              }}
                            >
                              Not found in candidate profile
                            </span>
                          )}
                        </div>
                        {note && (
                          <div
                            style={{
                              fontSize: "0.72rem",
                              fontStyle: "italic",
                              color: "#9a4a4a",
                              marginTop: 3,
                              lineHeight: 1.4,
                            }}
                          >
                            {note}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CompactCard>
          );
        })()}
      </div>

      {/* ============== 4. STRENGTHS / CONCERNS ============== */}
      {hasEvaluation && !aiUnavailable && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "1rem",
          }}
        >
          {/* "AI-Observed" prefix added per Sara May-23: distinguishes these
              AI-generated insights from the HR-entered Strengths/Concerns on
              the Feedback tab and the interviewer-entered ones on each
              Interview Round. Same word in 3 places was confusing HR. */}
          <CompactCard title="AI-Observed Strengths" icon="💪" accentColor="#2b7e3a">
            <CompactBullets
              items={evaluation.keyStrengths}
              icon="✓"
              color="#2b7e3a"
              cap={3}
            />
          </CompactCard>
          <CompactCard title="AI-Observed Concerns" icon="❗" accentColor="#c26b2c">
            <CompactBullets
              items={evaluation.keyConcerns}
              icon="⚠"
              color="#c26b2c"
              cap={3}
            />
          </CompactCard>
        </div>
      )}

      {/* ============== 5. WORK EXPERIENCE (full width — primary context) ============== */}
      <CompactCard title="Work Experience" icon="💼">
        {experiences.length === 0 ? (
          <Muted>No experience listed.</Muted>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {experiences.slice(0, 4).map((exp, idx) => (
              <ExperienceLine key={idx} exp={exp} />
            ))}
            {experiences.length > 4 && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: theme.textMuted,
                  fontStyle: "italic",
                }}
              >
                +{experiences.length - 4} earlier role
                {experiences.length - 4 === 1 ? "" : "s"}
              </span>
            )}
          </div>
        )}
      </CompactCard>

      {/* Section 6 (Top Highlights + Domain Expertise) removed — content is
          already covered by AI Decision (skill match %, domain fit), the
          Skills 2-column block, and the Strengths/Concerns block. */}

      {!loadingEvaluation && !hasEvaluation && (
        <div
          style={{
            background: "#fff7ed",
            borderRadius: "14px",
            padding: "8px 14px",
            borderLeft: "3px solid #f59e0b",
            color: "#92400e",
            fontSize: "0.82rem",
          }}
        >
          🤖 AI evaluation has not been generated for this applicant yet.
        </div>
      )}
    </div>
  );
}

TalentProfileTab.propTypes = {
  jobApplicationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  jobPostingSkills: PropTypes.array,
  isInternal: PropTypes.bool,
};

// ============================================================
// Helpers
// ============================================================

function KeyVal({ label, value, secondary }) {
  return (
    <div>
      <div
        style={{
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: "0.4px",
          color: theme.textLabel,
          fontWeight: 600,
          marginBottom: "3px",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "0.92rem",
          color: theme.text,
          fontWeight: 500,
          lineHeight: 1.35,
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
      {secondary && (
        <div
          style={{
            fontSize: "0.78rem",
            color: theme.textMuted,
            marginTop: "2px",
            lineHeight: 1.35,
          }}
        >
          {secondary}
        </div>
      )}
    </div>
  );
}
KeyVal.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  secondary: PropTypes.node,
};

function CompactCard({ title, icon, children, accentColor }) {
  return (
    <Card
      title={title}
      icon={icon}
      accentColor={accentColor}
      style={{ padding: "1rem 1.25rem", marginBottom: "1rem" }}
    >
      {children}
    </Card>
  );
}
CompactCard.propTypes = {
  title: PropTypes.node,
  icon: PropTypes.node,
  children: PropTypes.node,
  accentColor: PropTypes.string,
};

function CompactBullets({ items, icon, color, cap = 3 }) {
  if (!items || items.length === 0) {
    return <Muted>None listed.</Muted>;
  }
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {items.slice(0, cap).map((item, idx) => {
        const text =
          typeof item === "string"
            ? item
            : item?.description || item?.text || "";
        return (
          <li
            key={idx}
            style={{
              marginBottom: idx === Math.min(cap, items.length) - 1 ? 0 : "6px",
              fontSize: "0.85rem",
              paddingLeft: "1.4rem",
              position: "relative",
              lineHeight: 1.4,
              color: theme.text,
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 0,
                color,
                fontWeight: 700,
              }}
            >
              {icon}
            </span>
            {text}
          </li>
        );
      })}
      {items.length > cap && (
        <li
          style={{
            fontSize: "0.75rem",
            color: theme.textMuted,
            paddingLeft: "1.4rem",
            fontStyle: "italic",
            marginTop: "4px",
          }}
        >
          +{items.length - cap} more
        </li>
      )}
    </ul>
  );
}
CompactBullets.propTypes = {
  items: PropTypes.array,
  icon: PropTypes.string,
  color: PropTypes.string,
  cap: PropTypes.number,
};

function ExperienceLine({ exp }) {
  const start = exp.startDate
    ? new Date(exp.startDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
      })
    : "—";
  const end = exp.isCurrent
    ? "Present"
    : exp.endDate
    ? new Date(exp.endDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
      })
    : "—";

  const techs = exp.technologies || [];

  return (
    <div
      style={{
        padding: "6px 10px",
        background: "#fafdfb",
        borderLeft: "3px solid #2b7e3a",
        borderRadius: "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          gap: "8px",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "0.88rem", color: theme.accentDeep }}>
          {exp.jobTitle || "—"}
        </span>
        {exp.companyName && (
          <span style={{ fontSize: "0.82rem", color: theme.textMuted }}>
            @ {exp.companyName}
          </span>
        )}
        <span
          style={{
            fontSize: "0.72rem",
            color: theme.textLabel,
            marginLeft: "auto",
          }}
        >
          {start} – {end}
          {exp.isCurrent && " · Current"}
        </span>
      </div>
      {techs.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px",
            marginTop: "4px",
          }}
        >
          {techs.slice(0, 5).map((tech, idx) => (
            <span
              key={idx}
              style={{
                background: "#eef3fc",
                color: "#1a4c7a",
                padding: "1px 8px",
                borderRadius: "40px",
                fontSize: "0.68rem",
                fontWeight: 500,
              }}
            >
              {tech}
            </span>
          ))}
          {techs.length > 5 && (
            <span style={{ fontSize: "0.7rem", color: theme.textMuted }}>
              +{techs.length - 5}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
ExperienceLine.propTypes = { exp: PropTypes.object.isRequired };

function Muted({ children }) {
  return (
    <div style={{ color: theme.textMuted, fontSize: "0.83rem" }}>{children}</div>
  );
}
Muted.propTypes = { children: PropTypes.node };

// Format the AI evaluation's lastModifiedOn into a human-readable relative
// timestamp. Falls back to absolute date if the diff is over a week.
function formatLastUpdate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Strip directive phrasing from the AI's recommendation paragraph so it
// reads as advisory (per Sara May-23). Backend text often opens with
// "AI strongly recommends rejection..." / "AI strongly recommends moving
// forward..." which contradicts the soft "AI suggests" pill above. This
// helper rewrites the most directive phrases without changing the
// underlying intent.
function softenRecommendation(text) {
  if (!text) return text;
  return text
    .replace(/AI strongly recommends (rejection|rejecting)/gi, "AI suggests considering rejection")
    .replace(/AI strongly recommends moving forward/gi, "AI suggests prioritising this candidate for next steps")
    .replace(/AI strongly recommends/gi, "AI suggests")
    .replace(/strongly recommends rejection/gi, "suggests considering rejection")
    .replace(/strongly recommends moving forward/gi, "suggests prioritising the candidate")
    .replace(/\brecommends rejection\b/gi, "suggests considering rejection")
    .replace(/\brecommends moving forward\b/gi, "suggests prioritising for next steps")
    .replace(/\brecommends to reject\b/gi, "suggests considering rejection")
    .replace(/\bstrongly recommends\b/gi, "suggests");
}

// Map the backend's raw decision enum to non-directive language so HR sees
// the AI as advisory, not the deciding party (per Sara May-22).
//   move_forward / proceed   → "Strong Alignment"
//   reject                   → "Low Alignment"
//   needs_more_data          → "Needs Human Review"
// Anything else falls back to the prettified raw enum.
function softenDecision(raw) {
  if (!raw) return "—";
  const key = String(raw).toLowerCase();
  const MAP = {
    move_forward: "Strong Alignment",
    moveforward: "Strong Alignment",
    proceed: "Strong Alignment",
    strong: "Strong Alignment",
    strong_match: "Strong Alignment",
    reject: "Low Alignment",
    rejected: "Low Alignment",
    low_match: "Low Alignment",
    needs_more_data: "Needs Human Review",
    needsmoredata: "Needs Human Review",
    review: "Needs Human Review",
  };
  return MAP[key] || String(raw).toUpperCase().replace(/_/g, " ");
}

// =====================================================================
// ScoreBreakdownCard — surfaces how the AI fit score was computed
// so Hammad can audit the math (per Sai May-22). Backend sends:
//   { baseSkillScore, niceToHaveBonus, domainAlignmentBonus,
//     interviewContribution, performanceReviewContribution,
//     hardCapApplied }
// All values are numeric except hardCapApplied (string narrative).
// =====================================================================
function ScoreBreakdownCard({ breakdown, fitScore, isInternal }) {
  // Plain-English hint under each component label so HR doesn't have to
  // ask backend what each one means.
  // Performance Review tile only renders for internal applicants — per Sara
  // (May-22): "for external it should not count at all", showing 0 made it
  // look like the candidate lost points they could have earned.
  // Per Mudassir (May-26): BE now separates requiredSkills and
  // additionalSkills, so niceToHaveBonus can actually populate (>0) when
  // HR has added extras. Tile is back; renders 0/10 only when the posting
  // genuinely has no additional skills configured. (Earlier May-25 removal
  // was because the merging meant it was always 0 across every candidate.)
  const niceToHaveValue = Number(breakdown.niceToHaveBonus) || 0;
  const showNiceToHaveTile = niceToHaveValue !== 0;
  const rows = [
    {
      key: "baseSkillScore",
      label: "Base Skill Score",
      icon: "🎯",
      hint: "Match against the role's essential skills",
      max: 70,
    },
    ...(showNiceToHaveTile
      ? [
          {
            key: "niceToHaveBonus",
            label: "Nice-to-Have Bonus",
            icon: "✨",
            hint: "HR-added extra skills beyond the essentials",
            max: 10,
          },
        ]
      : []),
    {
      key: "domainAlignmentBonus",
      label: "Domain Alignment Bonus",
      icon: "🏢",
      hint: "Work history matches the role's industry",
      max: 5,
    },
    {
      key: "interviewContribution",
      label: "Interview Contribution",
      icon: "💬",
      // Per Sai (May-25): combines per-round HR ratings (1-10 scale, bucketed)
      // + final feedback evaluation (1-5 scale, applied as boost or penalty).
      // Per-round buckets: 9-10→+15, 7-8.99→+10, 5-6.99→0, 3-4.99→-10, 1-2.99→-20.
      // Feedback boosts/penalties layered on top.
      hint: "Combined score from per-round interview ratings + final HR feedback",
      max: 15,
    },
    ...(isInternal
      ? [
          {
            key: "performanceReviewContribution",
            label: "Performance Review",
            icon: "📈",
            hint: "Pulled from the employee's performance review",
            max: null,
          },
        ]
      : []),
  ];

  const sum = rows.reduce((acc, r) => acc + (Number(breakdown[r.key]) || 0), 0);
  const hardCap = breakdown.hardCapApplied;
  const sumMatchesFit = fitScore != null && Math.round(sum) === Math.round(fitScore);

  return (
    <div
      style={{
        background: theme.surface,
        borderRadius: "24px",
        padding: "1.1rem 1.4rem",
        marginBottom: "1rem",
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadowCard,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: "0.85rem",
            fontWeight: 700,
            color: theme.accentDeep,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          <span style={{ fontSize: "1.05rem" }}>🧮</span>
          Score Breakdown
        </div>
        <div
          style={{
            fontSize: "0.78rem",
            color: theme.textMuted,
          }}
        >
          Where the fit score comes from
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "10px",
          marginBottom: 12,
        }}
      >
        {rows.map((row) => {
          const val = breakdown[row.key];
          const num = Number(val) || 0;
          // Per Sara (May-22): backend may return negative values when the
          // candidate is below threshold (e.g. low interview ratings → -20).
          // Showing raw negatives is confusing for HR — clamp the displayed
          // value to 0 and swap the hint to communicate the gap instead.
          const isBelowThreshold = num < 0;
          const displayVal = isBelowThreshold ? 0 : num;
          // Per Sara (May-23): for internal applicants whose Performance
          // Review contribution is 0 (no review on file), show a clearer
          // message instead of "0" + the generic hint.
          const isNoReview =
            row.key === "performanceReviewContribution" &&
            isInternal &&
            num === 0;
          // Per Sai (May-25): for Interview Contribution, num === 0 means
          // either:
          //   (a) per-round ratings averaged in the 5.0-6.99 "neutral"
          //       bucket (decent but not strong), OR
          //   (b) a negative net (e.g. neutral rounds + weak feedback
          //       penalty) that got clamped to 0 for display.
          // Either way it's NOT "below threshold" or "no interviews".
          // Mention both inputs so HR understands the score reflects
          // rounds AND final feedback combined.
          const isInterviewNeutral =
            row.key === "interviewContribution" && num === 0;
          const hint = isNoReview
            ? "No performance review on file yet"
            : isInterviewNeutral
            ? "Interview ratings near the neutral 5–7 range and/or feedback below expectations — no contribution this round"
            : isBelowThreshold
            ? `Below threshold — ${row.label.toLowerCase()} did not contribute`
            : row.hint;
          return (
            <div
              key={row.key}
              style={{
                background: "#fafdfb",
                border: `1px solid ${theme.borderSoft}`,
                borderRadius: 14,
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: theme.textLabel,
                  fontWeight: 600,
                  marginBottom: 6,
                }}
              >
                <span style={{ marginRight: 4 }}>{row.icon}</span>
                {row.label}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color:
                      displayVal > 0
                        ? theme.accentDeep
                        : isBelowThreshold
                        ? "#b13e3e"
                        : theme.textMuted,
                    lineHeight: 1.1,
                  }}
                >
                  {displayVal > 0 ? "+" : ""}
                  {displayVal}
                </span>
                {row.max != null && (
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: theme.textLabel,
                      fontWeight: 500,
                    }}
                  >
                    / {row.max} max
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: "0.72rem",
                  color: isBelowThreshold ? "#b13e3e" : theme.textMuted,
                  lineHeight: 1.35,
                }}
              >
                {hint}
              </div>
            </div>
          );
        })}
      </div>

      {/* Total row — show the fit score cleanly. When a cap changed the
          score (sum != fitScore), add a small "adjusted" hint instead of
          showing the confusing "92 = 52/100" math. */}
      {(() => {
        const capNotTriggered =
          !hardCap || /^\s*none/i.test(String(hardCap));
        const capAdjustedScore = !sumMatchesFit && !capNotTriggered;
        return (
          <div
            style={{
              background: sumMatchesFit ? "#f0fdf4" : "#fff7ed",
              borderLeft: `4px solid ${sumMatchesFit ? "#22c55e" : "#f59e0b"}`,
              borderRadius: 12,
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              marginBottom: capAdjustedScore ? 10 : 0,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: sumMatchesFit ? "#15803d" : "#92400e",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Final Fit Score
              </span>
              {capAdjustedScore && (
                <span
                  style={{
                    fontSize: "0.72rem",
                    color: "#92400e",
                    fontStyle: "italic",
                  }}
                >
                  Adjusted from {Math.round(sum)} — score limit applied
                  (see below)
                </span>
              )}
            </div>
            <span
              style={{
                fontSize: "1.05rem",
                fontWeight: 700,
                color: sumMatchesFit ? "#15803d" : "#92400e",
              }}
            >
              {fitScore != null ? Math.round(fitScore) : Math.round(sum)} /100
            </span>
          </div>
        );
      })()}

      {/* Only show the score-limit explanation when a cap actually changed
          the score (per Sara May-22: hiding the "Not applied" noise). */}
      {hardCap && !/^\s*none/i.test(String(hardCap)) && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: "0.78rem",
            color: theme.textMuted,
            lineHeight: 1.5,
            paddingTop: 8,
            borderTop: `1px dashed ${theme.borderSoft}`,
            marginTop: 4,
          }}
        >
          <span style={{ fontSize: "0.95rem" }}>⚠️</span>
          <div>
            <span style={{ fontWeight: 700, color: theme.textLabel }}>
              Score limit applied:{" "}
            </span>
            <span>{hardCap}</span>
          </div>
        </div>
      )}
    </div>
  );
}
ScoreBreakdownCard.propTypes = {
  breakdown: PropTypes.object.isRequired,
  fitScore: PropTypes.number,
  isInternal: PropTypes.bool,
};

// =====================================================================
// RequiredSkillsAssessment — Per Sai (May-25): renders the AI's three
// classifications across two skill tiers:
//   • Essential (must-have): JobTitle / mandatory skills
//   • Nice-to-Have (bonus): HR-added "Additional Skills" extras
//
// Within each tier the AI returns three buckets:
//   • matched               (direct literal match in candidate's profile)
//   • matchedViaInference   (counted via implied-skill taxonomy e.g.
//                            "UI Design — inferred from React + CSS")
//   • missing               (still not covered)
//
// History:
//   - May-25: Nice-to-Have section was temporarily removed because BE
//     merged additional skills into essentials, so niceToHaveSkills was
//     always empty.
//   - May-26: Mudassir shipped separated `requiredSkills` + `additionalSkills`
//     fields, so AI now populates niceToHaveSkills properly. Section is
//     back, gracefully shows an empty hint when the posting has no extras
//     configured (which is still the case for most legacy postings).
//
// Inferred items render with a ✨ icon + tooltip that surfaces the AI's
// reasoning so HR can audit why a skill was counted without being literally
// listed on the candidate.
// =====================================================================
function RequiredSkillsAssessment({ essential, niceToHave }) {
  const eMatched = Array.isArray(essential?.matched) ? essential.matched : [];
  const eInferred = Array.isArray(essential?.matchedViaInference)
    ? essential.matchedViaInference
    : [];
  const eMissing = Array.isArray(essential?.missing) ? essential.missing : [];
  const nMatched = Array.isArray(niceToHave?.matched) ? niceToHave.matched : [];
  const nInferred = Array.isArray(niceToHave?.matchedViaInference)
    ? niceToHave.matchedViaInference
    : [];
  const nMissing = Array.isArray(niceToHave?.missing) ? niceToHave.missing : [];

  const essentialCovered = eMatched.length + eInferred.length;
  const essentialTotal = essentialCovered + eMissing.length;
  const niceCovered = nMatched.length + nInferred.length;
  const niceTotal = niceCovered + nMissing.length;
  const hasNiceToHaveData = niceTotal > 0;

  const accent =
    eMissing.length === 0 && essentialTotal > 0 ? "#2b7e3a" : "#b13e3e";

  return (
    <CompactCard
      title="Role Skills · Match Assessment"
      icon="📋"
      accentColor={accent}
    >
      {/* ---------- Essential block (prominent) ---------- */}
      <SkillsSubsection
        heading="Essential Skills"
        headingIcon="🎯"
        covered={essentialCovered}
        total={essentialTotal}
        matched={eMatched}
        matchedViaInference={eInferred}
        missing={eMissing}
        priority="essential"
        emptyHint="No essential skills configured for this role."
      />

      {/* ---------- Nice-to-Have block (secondary) ----------
          Only render when the posting actually has nice-to-have skills
          configured. For postings where HR hasn't filled the "Additional
          Skills" dropdown, the section stays hidden so HR doesn't see an
          empty placeholder. */}
      {hasNiceToHaveData && (
        <>
          <div
            style={{
              borderTop: `1px dashed ${theme.borderSoft}`,
              margin: "14px 0",
            }}
          />
          <SkillsSubsection
            heading="Nice-to-Have Skills"
            headingIcon="✨"
            covered={niceCovered}
            total={niceTotal}
            matched={nMatched}
            matchedViaInference={nInferred}
            missing={nMissing}
            priority="niceToHave"
            emptyHint="No nice-to-have skills listed."
          />
        </>
      )}
    </CompactCard>
  );
}
RequiredSkillsAssessment.propTypes = {
  essential: PropTypes.object,
  niceToHave: PropTypes.object,
};

function SkillsSubsection({
  heading,
  headingIcon,
  covered,
  total,
  matched,
  matchedViaInference,
  missing,
  priority,
  emptyHint,
}) {
  const isEssential = priority === "essential";
  const hasAny =
    matched.length + matchedViaInference.length + missing.length > 0;
  if (!hasAny) {
    return (
      <div>
        <SkillsSubsectionHeading
          heading={heading}
          icon={headingIcon}
          covered={covered}
          total={total}
        />
        <Muted>{emptyHint}</Muted>
      </div>
    );
  }

  return (
    <div>
      <SkillsSubsectionHeading
        heading={heading}
        icon={headingIcon}
        covered={covered}
        total={total}
      />

      {/* Matched skills (direct + inferred share the same row — inferred
          are visually marked with ✨ + tooltip explaining why). */}
      {(matched.length > 0 || matchedViaInference.length > 0) && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 5,
            marginBottom: missing.length > 0 ? 10 : 0,
          }}
        >
          {matched.map((skill, idx) => (
            <span
              key={`m-${idx}`}
              style={{
                padding: "3px 10px",
                borderRadius: 40,
                fontSize: "0.75rem",
                fontWeight: 500,
                background: "#dff5e3",
                color: "#0f5a2a",
                border: "1.5px solid #2b7e3a",
              }}
            >
              ✓ {skill}
            </span>
          ))}
          {matchedViaInference.map((item, idx) => {
            const { name, inference } = parseInferredSkill(item);
            return (
              <Tooltip
                key={`i-${idx}`}
                // Two-line tooltip: short header (scannable) + body
                // (reasoning + explanation of partial-credit). HR who hover
                // briefly get the gist; deeper readers see the full why.
                title={
                  <div style={{ fontSize: "0.78rem", lineHeight: 1.5 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        marginBottom: 6,
                        color: "#fff",
                      }}
                    >
                      ✨ Inferred match — partial scoring credit
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.92)" }}>
                      {inference
                        ? `${name} isn't directly listed in the candidate's profile but the AI inferred it from related work: ${inference}.`
                        : `${name} was counted via the implied-skill taxonomy.`}
                    </div>
                    <div
                      style={{
                        marginTop: 8,
                        paddingTop: 8,
                        borderTop: "1px dashed rgba(255,255,255,0.3)",
                        color: "rgba(255,255,255,0.85)",
                        fontStyle: "italic",
                      }}
                    >
                      Counted as matched for coverage, but receives partial
                      credit in scoring since it&apos;s not directly verified
                      — this is why the Essential % can show below 100% even
                      when all skills appear covered.
                    </div>
                  </div>
                }
                placement="top"
                overlayStyle={{ maxWidth: 360 }}
              >
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: 40,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    background: "#eef3fc",
                    color: "#1a4c7a",
                    border: "1.5px dashed #5a8bcf",
                    cursor: "help",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  ✨ {name}
                  <InfoCircleOutlined style={{ fontSize: 10, opacity: 0.75 }} />
                </span>
              </Tooltip>
            );
          })}
        </div>
      )}

      {/* Missing skills — essential gets the strong-red treatment;
          nice-to-have is muted orange so HR knows the priority. */}
      {missing.length > 0 && (
        <div>
          <div
            style={{
              fontSize: "0.65rem",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 700,
              color: isEssential ? "#b13e3e" : "#92400e",
              marginBottom: 4,
            }}
          >
            ✗ Missing · {missing.length}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {missing.map((skill, idx) => (
              <span
                key={`x-${idx}`}
                style={{
                  padding: "3px 10px",
                  borderRadius: 40,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  background: isEssential ? "#fff0ee" : "#fff7ed",
                  color: isEssential ? "#7a1f1f" : "#7a4a10",
                  border: isEssential
                    ? "1.5px solid #b13e3e"
                    : "1px solid #fcd34d",
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
SkillsSubsection.propTypes = {
  heading: PropTypes.string.isRequired,
  headingIcon: PropTypes.string,
  covered: PropTypes.number,
  total: PropTypes.number,
  matched: PropTypes.array.isRequired,
  matchedViaInference: PropTypes.array.isRequired,
  missing: PropTypes.array.isRequired,
  priority: PropTypes.oneOf(["essential", "niceToHave"]).isRequired,
  emptyHint: PropTypes.string,
};

function SkillsSubsectionHeading({ heading, icon, covered, total }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 8,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          fontSize: "0.78rem",
          fontWeight: 700,
          color: theme.accentDeep,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        <span style={{ marginRight: 5 }}>{icon}</span>
        {heading}
      </div>
      {total > 0 && (
        <div
          style={{
            fontSize: "0.72rem",
            fontWeight: 600,
            color: covered === total ? "#15803d" : theme.textMuted,
          }}
        >
          {covered} / {total} matched
        </div>
      )}
    </div>
  );
}
SkillsSubsectionHeading.propTypes = {
  heading: PropTypes.string,
  icon: PropTypes.string,
  covered: PropTypes.number,
  total: PropTypes.number,
};

// Parse the AI's inferred-skill string into name + reason. Backend format
// is "SkillName — explanation" (em-dash). Falls back to "Name - reason"
// (regular dash with spaces) for safety, and returns the raw string as
// the name with no inference reason if neither separator is found.
function parseInferredSkill(text) {
  if (!text) return { name: "", inference: null };
  const str = String(text);
  const emDashIdx = str.indexOf(" — ");
  if (emDashIdx > 0) {
    return {
      name: str.slice(0, emDashIdx).trim(),
      inference: str.slice(emDashIdx + 3).trim(),
    };
  }
  const dashIdx = str.indexOf(" - ");
  if (dashIdx > 0) {
    return {
      name: str.slice(0, dashIdx).trim(),
      inference: str.slice(dashIdx + 3).trim(),
    };
  }
  return { name: str, inference: null };
}

// Inline placeholder used when a Core Identity field is missing.
function NotProvided() {
  return (
    <span
      style={{
        color: theme.textLabel,
        fontStyle: "italic",
        fontWeight: 400,
        fontSize: "0.85rem",
      }}
    >
      Not provided
    </span>
  );
}

const linkStyle = {
  color: "#1f4b8a",
  textDecoration: "none",
  wordBreak: "break-all",
};

export default TalentProfileTab;
